import { CommonActivityType } from './activity-history/common-activity-type.enum';

/**
 * Activity types tracked in the license_activity table.
 * Extends CommonActivityType with License-specific types.
 */
export const LicenseActivityType = {
	...CommonActivityType,
	// License-specific types
	DISCIPLINARY_ACTION_CREATED: 'disciplinary_action_created',
	DISCIPLINARY_ACTION_UPDATED: 'disciplinary_action_updated',
	DISCIPLINARY_ACTION_DELETED: 'disciplinary_action_deleted',
	// Match-related types
	MATCH_CONFIRMED: 'match_confirmed',
	MATCH_REJECTED: 'match_rejected',
	MATCH_REVERTED: 'match_reverted',
	MATCH_MANUAL: 'match_manual',
	// Registry sync types
	REGISTRY_STATUS_CHANGED: 'registry_status_changed',
	// Link drift (2026-09-05): the regulator stopped linking this licence to a
	// licence the client also holds (e.g. the vet left the premise).
	LINK_ENDED: 'link_ended',
} as const;

export type LicenseActivityType =
	(typeof LicenseActivityType)[keyof typeof LicenseActivityType];

export type LicenseActivityTypeValue = LicenseActivityType;

/**
 * Labels for activity types (for display in filter bar)
 */
export const LICENSE_ACTIVITY_TYPE_LABELS: Record<LicenseActivityType, string> =
	{
		// Common types
		[LicenseActivityType.CREATED]: 'Created',
		[LicenseActivityType.UPDATED]: 'Updated',
		[LicenseActivityType.DELETED]: 'Deleted',
		[LicenseActivityType.RECOVERED]: 'Recovered',
		[LicenseActivityType.STATUS_CHANGED]: 'Status Changed',
		[LicenseActivityType.PRIORITY_CHANGED]: 'Priority Changed',
		[LicenseActivityType.ASSIGNEE_CHANGED]: 'Assignee Changed',
		[LicenseActivityType.NOTE_ADDED]: 'Note Added',
		[LicenseActivityType.NOTE_UPDATED]: 'Note Updated',
		[LicenseActivityType.NOTE_DELETED]: 'Note Deleted',
		[LicenseActivityType.TASK_LINKED]: 'Task Linked',
		[LicenseActivityType.TASK_UNLINKED]: 'Task Unlinked',
		[LicenseActivityType.TASK_CREATED]: 'Task Created',
		[LicenseActivityType.TASK_COMPLETED]: 'Task Completed',
		[LicenseActivityType.TASK_UPDATED]: 'Task Updated',
		[LicenseActivityType.TASK_DELETED]: 'Task Deleted',
		[LicenseActivityType.FILE_ATTACHED]: 'File Attached',
		[LicenseActivityType.FILE_REMOVED]: 'File Removed',
		[LicenseActivityType.ENTITY_LINKED]: 'Entity Linked',
		[LicenseActivityType.ENTITY_UNLINKED]: 'Entity Unlinked',
		[LicenseActivityType.WATCHER_ADDED]: 'Watcher Added',
		[LicenseActivityType.WATCHER_REMOVED]: 'Watcher Removed',
		[LicenseActivityType.AI_SESSION]: 'AI Session',
		// License-specific types
		[LicenseActivityType.DISCIPLINARY_ACTION_CREATED]:
			'Disciplinary Action Created',
		[LicenseActivityType.DISCIPLINARY_ACTION_UPDATED]:
			'Disciplinary Action Updated',
		[LicenseActivityType.DISCIPLINARY_ACTION_DELETED]:
			'Disciplinary Action Deleted',
		// Match-related labels
		[LicenseActivityType.MATCH_CONFIRMED]: 'Match Confirmed',
		[LicenseActivityType.MATCH_REJECTED]: 'Match Rejected',
		[LicenseActivityType.MATCH_REVERTED]: 'Match Reverted',
		[LicenseActivityType.MATCH_MANUAL]: 'Manual Match',
		[LicenseActivityType.REGISTRY_STATUS_CHANGED]: 'Registry Status Changed',
		[LicenseActivityType.LINK_ENDED]: 'Regulator Link Ended',
	};

/**
 * Group activity types for filter bar categories
 */
export const LICENSE_ACTIVITY_GROUPS = {
	all: [] as Array<LicenseActivityType>,
	status: [
		LicenseActivityType.CREATED,
		LicenseActivityType.UPDATED,
		LicenseActivityType.STATUS_CHANGED,
		LicenseActivityType.ASSIGNEE_CHANGED,
	],
	disciplinary: [
		LicenseActivityType.DISCIPLINARY_ACTION_CREATED,
		LicenseActivityType.DISCIPLINARY_ACTION_UPDATED,
		LicenseActivityType.DISCIPLINARY_ACTION_DELETED,
	],
	notes: [
		LicenseActivityType.NOTE_ADDED,
		LicenseActivityType.NOTE_UPDATED,
		LicenseActivityType.NOTE_DELETED,
	],
	tasks: [
		LicenseActivityType.TASK_LINKED,
		LicenseActivityType.TASK_UNLINKED,
		LicenseActivityType.TASK_CREATED,
		LicenseActivityType.TASK_COMPLETED,
	],
	files: [LicenseActivityType.FILE_ATTACHED, LicenseActivityType.FILE_REMOVED],
	watchers: [
		LicenseActivityType.WATCHER_ADDED,
		LicenseActivityType.WATCHER_REMOVED,
	],
	matching: [
		LicenseActivityType.MATCH_CONFIRMED,
		LicenseActivityType.MATCH_REJECTED,
		LicenseActivityType.MATCH_REVERTED,
		LicenseActivityType.MATCH_MANUAL,
		LicenseActivityType.REGISTRY_STATUS_CHANGED,
		LicenseActivityType.LINK_ENDED,
	],
};
